"use client";

import dynamic from "next/dynamic";
import { useCallback, useMemo, useState } from "react";
import type { Session } from "next-auth";
import { useSession } from "next-auth/react";

import { errorIntoString } from "@/lib/utils";
import { ga4SaveItem } from "@/lib/analytics/ga4-ecommerce";
import type { BlogArticle, BlogsCollectionResponse } from "@/types";
import {
	useGetSavedBlogsQuery,
	useSetSavedBlogsMutation,
} from "@/features/saved/savedApiInjections";

import { useToast } from "@/components/ui/use-toast";
import SignupPromptModal from "@/components/auth/SignupPromptModal";
import ArticleCard from "@/components/cards/ArticleCard";
import FilterPopup from "@/app/blogs/components/FilterPopup";
import SortByDropdown from "@/components/menu/SortByDropdown";
import { BLOG_SORTS } from "@/components/menu/SortByDropdown/sorts";

const PaginationBar = dynamic(() => import("@/components/PaginationBar"), {
	ssr: false,
});

type Props = {
	data: BlogsCollectionResponse;
	/** @deprecated Session is resolved on the client via `useSession` for edge-cacheable pages. */
	session?: Session | null;
};

export default function ArticlesList({ data, session: sessionProp }: Props) {
	const { data: clientSession } = useSession();
	const session = sessionProp !== undefined ? sessionProp : (clientSession ?? null);
	const { toast } = useToast();
	
	// CRITICAL: Memoize the user ID to prevent query argument from changing on every render
	const userId = useMemo(() => session?.user?.id ?? null, [session?.user?.id]);
	
	// Only fetch if user is signed in and has a valid session token
	const hasValidSession = session?.idToken && session.idToken.trim().length > 0;
	
	const { data: saved, isLoading, error } = useGetSavedBlogsQuery(userId ?? "", {
		skip: userId === null || !hasValidSession,
		// Don't refetch on mount if we already have data or if there's an error
		refetchOnMountOrArgChange: false,
		refetchOnFocus: false,
		refetchOnReconnect: false,
	});
	const [setFav, { isLoading: isFavoriteUpdating }] = useSetSavedBlogsMutation();
	const [showSignupModal, setShowSignupModal] = useState(false);
	const [signupReturnUrl, setSignupReturnUrl] = useState<string | undefined>(undefined);
	
	// Use a resolved loading state - if query is skipped, it's not loading
	const isActuallyLoading = !session || !session.user?.id ? false : isLoading;

	const handleFavoriteClick = useCallback(
		async (article: BlogArticle) => {
			if (!session || !hasValidSession) {
				setSignupReturnUrl(
					typeof window === "undefined"
						? undefined
						: `${window.location.pathname}${window.location.search}`,
				);
				setShowSignupModal(true);
				return;
			}

			if (saved === undefined) return;
			const isFavorite = saved.blogIds.find((x) => x._id === article._id);

			if (isFavorite) {
				const resp = await setFav({
					userId: session.user.id,
					blogIds: saved.blogIds.filter((x) => x._id !== article._id).map((x) => x._id),
				});

				if ("error" in resp) {
					const ex = errorIntoString(resp.error);
					toast({
						variant: "destructive",
						title: `Failed to remove ${article.title} from saved`,
						description: ex,
					});

					return;
				}

				toast({ title: `${article.title} removed from saved` });
				ga4SaveItem({
					content_type: "blog",
					item_id: article._id,
					item_name: article.title,
					saved: false,
				});
				return;
			}

			const resp = await setFav({
				userId: session.user.id,
				blogIds: [...saved.blogIds.map((id) => id._id), article._id],
			});

			if ("error" in resp) {
				const ex = errorIntoString(resp.error);
				toast({
					variant: "destructive",
					title: `Failed to add ${article.title} to saved`,
					description: ex,
				});
				return;
			}

			toast({ title: `${article.title} saved` });
			ga4SaveItem({
				content_type: "blog",
				item_id: article._id,
				item_name: article.title,
				saved: true,
			});
		},
		[hasValidSession, saved, session, setFav, toast],
	);

	if (data.docs.length === 0) {
		return (
			<div className="container flex min-h-64 flex-col items-center justify-center">
				<h1 className="text-2xl font-semibold">No Content Available</h1>
			</div>
		);
	}

	return (
		<div className="container flex flex-col gap-y-6 pb-8 pt-4 sm:gap-y-2 sm:pt-0">
			<div className="grid grid-cols-[1fr] items-center gap-y-4 sm:grid-cols-[1fr_auto] sm:grid-rows-[5rem] sm:gap-x-2 sm:gap-y-0">
				<span className="text-sm sm:self-center">
					Showing {data.docs.length} of {data.totalDocs} results
				</span>
				<div className="flex h-10 w-full min-w-0 items-center justify-end gap-2 sm:w-auto sm:self-center">
					<div className="shrink-0">
						<SortByDropdown
							defaultSort="default"
							removeParamWhen="default"
							sorts={BLOG_SORTS}
							glassToolbar
						/>
					</div>
					<div className="shrink-0">
						<FilterPopup />
					</div>
				</div>
			</div>

			<div className="grid grid-cols-1 justify-center gap-8 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fill,18rem)] xl:gap-x-4 2xl:grid-cols-[repeat(auto-fill,20rem)] 2xl:gap-x-8">
				{data.docs.map((x) => (
					<ArticleCard
						key={x._id}
						session={session}
						data={x}
						favorite={{
							isLoading: isActuallyLoading,
							isUpdating: isFavoriteUpdating,
							isFavorite:
								saved === undefined
									? false
									: saved?.blogIds.findIndex((z) => z._id === x._id) !== -1,
							onClick: handleFavoriteClick,
						}}
					/>
				))}
			</div>

			<PaginationBar totalPages={data.totalPages} />
			<SignupPromptModal
				open={showSignupModal}
				onOpenChange={setShowSignupModal}
				returnUrl={signupReturnUrl}
				title="Sign up to save this blog"
				description="Create a Techxeed account before saving this blog."
			/>
		</div>
	);
}
